import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const { FiChevronDown, FiChevronUp, FiHelpCircle } = FiIcons;

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "Why is this invite-only?",
      answer: "We're looking for a small group of real users to help shape the platform as we prepare for public launch. This allows us to provide personalized support and gather valuable feedback to improve the product."
    },
    {
      question: "Why do I see \"Agency Owner\" in my workspace?",
      answer: "That's us. We set up your workspace for you, just like a done-for-you content partner. It helps us prepare your brand's voice, visuals, and sample posts so you can get started instantly. You stay in full control — we only handle the initial setup, and you can customize everything as you like."
    },
    {
      question: "Do you offer refunds after payment?",
      answer: "Since we offer a 15-day free trial with full access, we don't offer refunds once a subscription begins. This gives you plenty of time to explore everything Yiksy offers before committing. If you face any issues or need help, our team is always here to support."
    },
    {
      question: "Which platforms can I post to?",
      answer: "You can schedule and publish to LinkedIn, Instagram, Facebook, X (Twitter), TikTok, Pinterest, and Google My Business with YouTube coming soon."
    },
    {
      question: "Can I edit the content or graphics before posting?",
      answer: "Yes, every post is editable. You can tweak the captions, switch templates, or adjust designs with our built-in editor."
    },
    {
      question: "What happens after beta?",
      answer: "You keep your special rate and get access to all new features as the platform grows. Beta users will always enjoy preferred pricing and priority support as a thank you for joining us early."
    },
    {
      question: "How is support handled?",
      answer: "Yes, we offer email support for all users. Since we're a small team, responses may take up to 24-48 hours, but we do our best to help as quickly as possible."
    },
    {
      question: "Do I need to enter payment details for the trial?",
      answer: "Nope. Enjoy the full platform for 15 days, completely free—no credit card required. We'll send you a reminder before your trial ends so you can decide if you want to continue."
    }
  ];

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="bg-bg-light py-16 sm:py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute bottom-0 right-0 w-48 sm:w-64 lg:w-96 h-48 sm:h-64 lg:h-96 bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl opacity-70 translate-x-1/3 translate-y-1/3"></div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <SafeIcon icon={FiHelpCircle} className="mr-2" />
            Common Questions
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6 px-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg sm:text-xl text-text-light max-w-2xl mx-auto px-4">
            Everything you need to know about the beta program
          </p>
        </motion.div>

        <motion.div 
          className="space-y-4 sm:space-y-6"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className={`bg-white border rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-300 mx-4 sm:mx-0 ${
                openIndex === index 
                  ? 'border-primary shadow-lg' 
                  : 'border-gray-200'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <button
                className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors touch-manipulation"
                onClick={() => handleToggle(index)}
              >
                <span className="text-base sm:text-lg font-bold text-text pr-4">
                  {faq.question}
                </span>
                <motion.div
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
                    openIndex === index ? 'bg-primary' : 'bg-gray-100'
                  }`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <SafeIcon
                    icon={openIndex === index ? FiChevronUp : FiChevronDown}
                    className={`${
                      openIndex === index ? 'text-white' : 'text-text-light'
                    } text-base sm:text-lg`}
                  />
                </motion.div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 lg:pb-8 border-t border-gray-100 pt-4 sm:pt-6">
                      <p className="text-text-light leading-relaxed text-sm sm:text-base">
                        {faq.answer}
                      </p>
                      
                      {/* Only show for the first FAQ */}
                      {index === 0 && (
                        <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 rounded-lg sm:rounded-xl">
                          <div className="flex">
                            <div className="flex-shrink-0 mr-3 sm:mr-4">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <SafeIcon icon={FiHelpCircle} className="text-primary text-sm sm:text-base" />
                              </div>
                            </div>
                            <div>
                              <h5 className="font-medium text-text text-sm sm:text-base">Spots are limited</h5>
                              <p className="text-xs sm:text-sm text-text-light">
                                We're only accepting 500 beta users to ensure quality support and feedback collection.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;